import { useState } from "react";
import { Link } from "react-router-dom";
import "./mutual-fund-hero.css";

const RANGES = ["1M", "6M", "1Y", "3Y", "All"];

const HOLDINGS = [
  { label: "Equity Funds", pct: 60, color: "#139bb2" },
  { label: "Debt Funds", pct: 25, color: "#3b82f6" },
  { label: "Hybrid Funds", pct: 15, color: "#f59e0b" },
];

const FEATURES = [
  {
    icon: "fa-chart-pie",
    title: "Expert Fund Research",
    desc: "In-depth analysis to identify top-performing funds.",
  },
  {
    icon: "fa-user-plus",
    title: "Personalized Planning",
    desc: "Goal-based investment plans tailored to your needs.",
  },
  {
    icon: "fa-desktop",
    title: "Portfolio Monitoring",
    desc: "Continuous tracking and rebalancing for optimal performance.",
  },
  {
    icon: "fa-file-invoice-dollar",
    title: "Tax Efficient Investing",
    desc: "Smart tax-saving solutions to help you keep more of your returns.",
  },
  {
    icon: "fa-headset",
    title: "Dedicated Relationship Manager",
    desc: "One-on-one support at every step of your investment journey.",
  },
];

const AVATARS = [
  "/images/user1.jpg",
  "/images/user2.jpg",
  "/images/user3.jpg",
  "/images/user1.jpg",
];

function LineChart() {
  return (
    <svg
      className="mf-hero-chart"
      viewBox="0 0 360 120"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="mfChartFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#139bb2" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#139bb2" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M0 95 C40 90, 55 70, 90 65 C130 58, 150 78, 180 55 C210 35, 240 42, 270 28 C300 16, 330 22, 360 12 L360 120 L0 120 Z"
        fill="url(#mfChartFill)"
      />
      <path
        d="M0 95 C40 90, 55 70, 90 65 C130 58, 150 78, 180 55 C210 35, 240 42, 270 28 C300 16, 330 22, 360 12"
        fill="none"
        stroke="#139bb2"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx="360" cy="12" r="5" fill="#139bb2" />
    </svg>
  );
}

function DonutChart() {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <svg width="96" height="96" viewBox="0 0 96 96" aria-hidden="true">
      <circle cx="48" cy="48" r={radius} fill="none" stroke="#eef2f6" strokeWidth="12" />
      {HOLDINGS.map((h) => {
        const length = (h.pct / 100) * circumference;
        const el = (
          <circle
            key={h.label}
            cx="48"
            cy="48"
            r={radius}
            fill="none"
            stroke={h.color}
            strokeWidth="12"
            strokeDasharray={`${length} ${circumference - length}`}
            strokeDashoffset={-offset}
            transform="rotate(-90 48 48)"
            strokeLinecap="butt"
          />
        );
        offset += length;
        return el;
      })}
      <circle cx="48" cy="48" r="24" fill="#fff" />
    </svg>
  );
}

export default function MutualFundHero() {
  const [range, setRange] = useState("1Y");

  const handleStartInvesting = (e) => {
    const encryptedData = localStorage.getItem(
      process.env.REACT_APP_LOGIN_DATA_STORE
    );
    if (!encryptedData) {
      e.preventDefault();
      window.location.href = "https://gpsfinvest.investwell.app/app/#/login";
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  };

  return (
    <section className="mf-hero">
      <div className="mf-hero-inner">
        <div className="mf-hero-top">
          <div className="mf-hero-copy">
            <p className="mf-hero-eyebrow">MUTUAL FUNDS</p>
            <h1 className="mf-hero-title">
              Invest Smarter,{" "}
              <span className="highlight">Grow Wealth</span> Confidently
            </h1>
            <p className="mf-hero-desc">
              We help you invest in the right mutual funds with expert research,
              disciplined strategies, and personalized guidance to build
              long-term wealth.
            </p>

            <div className="mf-hero-ctas">
              <Link
                to="/clientmutualfunds"
                className="mf-hero-btn-primary"
                onClick={handleStartInvesting}
              >
                Start Investing <span aria-hidden="true">→</span>
              </Link>
              <Link to="/contact" className="mf-hero-btn-secondary">
                <i className="fa-solid fa-phone" aria-hidden="true" />
                Talk to an Advisor
              </Link>
            </div>

            {/* <div className="mf-hero-trust">
              <div className="mf-hero-avatars">
                {AVATARS.map((src, i) => (
                  <img key={i} src={src} alt="" />
                ))}
              </div>
              <p className="mf-hero-trust-text">
                <strong>Trusted by 10,000+</strong>
                <span>Investors Worldwide</span>
              </p>
            </div> */}
          </div>

          <div className="mf-hero-visual">
            <img
              className="mf-hero-illustration"
              src="/images/mutual-illustration.png"
              alt=""
              aria-hidden="true"
            />

            <div className="mf-portfolio-card">
              <div className="mf-portfolio-head">
                <h3>Portfolio Overview</h3>
                <div className="mf-portfolio-ranges" role="tablist">
                  {RANGES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      role="tab"
                      aria-selected={range === r}
                      className={range === r ? "active" : ""}
                      onClick={() => setRange(r)}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mf-portfolio-value">
                <span className="label">Current Value</span>
                <div className="mf-portfolio-value-row">
                  <strong>₹ 24,75,450</strong>
                  <span className="gain">▲ 18.8% ({range})</span>
                </div>
              </div>

              <LineChart />

              <div className="mf-portfolio-holdings">
                <div className="mf-holdings-list">
                  <p className="mf-holdings-label">Top Holdings</p>
                  {HOLDINGS.map((h) => (
                    <div key={h.label} className="mf-holding-row">
                      <span
                        className="dot"
                        style={{ background: h.color }}
                      />
                      <span className="name">{h.label}</span>
                      <span className="pct">{h.pct}%</span>
                    </div>
                  ))}
                </div>
                <DonutChart />
              </div>
            </div>
          </div>
        </div>

        <div className="mf-hero-features">
          {FEATURES.map((f) => (
            <div key={f.title} className="mf-hero-feature">
              <div className="mf-hero-feature-icon">
                <i className={`fa-solid ${f.icon}`} aria-hidden="true" />
              </div>
              <h4>{f.title}</h4>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
